import React from 'react';
import {
  ArrowRight,
  Check,
  Copy,
  FileText,
  Filter,
  Loader2,
  X,
} from 'lucide-react';
import AnswersEvidenceTray from './AnswersEvidenceTray';
import { AnswersRagScopeChips } from './answersRagFilters';
import {
  formatFilterStatsLine,
  formatResponseSeconds,
  isNoInformationAnswer,
} from '../answersService';
import { MarkdownLite, PdfCitationViewer } from './AnswersEvidenceView';

const AnswersWorkspace = ({
  activeFiltersCount,
  activeScopeChips,
  conversation,
  copiedId,
  error,
  filtersMatchText,
  filtersOpen,
  handleCitationClick,
  handlePdfLoadError,
  handlePdfLoadSuccess,
  inputText,
  isQuerying,
  morph,
  onClearFilters,
  onCopyAnswer,
  onToggleFilters,
  onSendFollowUp,
  onSetInputText,
  onSetPdfLoadError,
  onSetSelectedSource,
  onSuggestedQuestion,
  pdfLoadError,
  referencesForTurn,
  scrollRef,
  selectedPdfUrl,
  selectedSource,
  targetInputRef,
  updateTurn,
}) => {
  const activeTurn = conversation[conversation.length - 1] || null;

  return (
    <div className="answers-copy-shell">
      <div className="answers-copy-view">
        <header className="answers-header">
          <div className="ah-left">
            <div className="ah-title-row">
              <h1 className="ah-title">
                {activeTurn?.question
                  ? `Memorandum: ${activeTurn.question.substring(0, 40)}${activeTurn.question.length > 40 ? '...' : ''}`
                  : 'New Draft Analysis'}
              </h1>
            </div>
            <AnswersRagScopeChips chips={activeScopeChips} />
            <div className="ah-meta">
              <span>
                Created {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
          <div className="ah-right">
            {activeFiltersCount > 0 && (
              <div className="answers-copy-filter-summary" title={filtersMatchText}>
                <span>{activeFiltersCount} active</span>
                <small>{filtersMatchText}</small>
              </div>
            )}
            {activeFiltersCount > 0 && (
              <button type="button" className="btn btn-sm btn-clear-filters" onClick={onClearFilters}>
                <X size={14} className="btn-clear-filters-icon" />
                <span>Clear</span>
              </button>
            )}
            <div className="filter-btn-wrap">
              <button
                type="button"
                className={`btn btn-sm ${filtersOpen ? 'btn-primary' : ''} btn-filters`}
                onClick={onToggleFilters}
              >
                <Filter size={14} />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span
                    className="btn-filters-dot"
                    style={{ background: filtersOpen ? '#fff' : 'var(--c-black)' }}
                  />
                )}
              </button>
            </div>
          </div>
        </header>

        <main className="answers-body">
          <aside className="ap-left">
            <div className="ap-left-scroll" ref={scrollRef}>
              {conversation.map((turn, idx) => {
                const references = referencesForTurn(turn);
                const answerText = turn.response?.answer || turn.streamingAnswer || '';
                const isLatestLoading = isQuerying && idx === conversation.length - 1;
                const responseSeconds = formatResponseSeconds(turn.response?.response_time_ms);
                const filterStatsLine = formatFilterStatsLine(turn.response?.filter_stats);

                return (
                  <React.Fragment key={turn.id}>
                    <div className="answers-copy-user-bubble">{turn.question}</div>

                    <div className={`version-card ${idx === conversation.length - 1 ? 'active' : ''}`}>
                      <div className="vc-header">
                        {responseSeconds && (
                          <span className="vc-response-time">Response Time: {responseSeconds}</span>
                        )}
                        <div className="mi-assistant-head">
                          <span className="mi-assistant-avatar" aria-hidden="true">IA</span>
                          <span className="mi-assistant-name">Koby</span>
                        </div>
                      </div>

                      {(turn.thinking || (isLatestLoading && !turn.streamingAnswer && !turn.response)) && (
                        <section className="answers-thinking-card answers-thinking-card--copy">
                          <button
                            type="button"
                            onClick={() => updateTurn(turn.id, (t) => ({ ...t, thinkingExpanded: !t.thinkingExpanded }))}
                          >
                            <span>Thinking</span>
                            <span>{turn.thinkingExpanded ? 'Hide' : 'Show'}</span>
                          </button>
                          {(turn.thinkingExpanded || (isLatestLoading && !turn.streamingAnswer && !turn.response)) && (
                            <div className="answers-thinking-body">
                              {turn.thinking || (
                                <div className="answers-loading-inline">
                                  <span />
                                  <span />
                                  <span />
                                  <em>Analyzing your question and searching through filings...</em>
                                </div>
                              )}
                            </div>
                          )}
                        </section>
                      )}

                      <div className="vc-content">
                        {answerText ? (
                          <div className="answers-md answers-md--copy">
                            <MarkdownLite text={answerText} turn={turn} onCitationClick={handleCitationClick} />
                            {turn.streamingAnswer && !turn.response && (
                              <span className="answers-md-cursor">|</span>
                            )}
                          </div>
                        ) : (
                          <div className="vc-content-placeholder">
                            {isLatestLoading ? 'Analyzing datasets...' : ''}
                          </div>
                        )}
                      </div>

                      <div className="vc-footer">
                        <div className="vc-actions">
                          {copiedId === turn.id ? (
                            <Check size={13} color="var(--c-teal-active)" className="vc-action-btn" />
                          ) : (
                            <Copy
                              size={13}
                              className="vc-action-btn"
                              title="Copy answer to clipboard"
                              onClick={() => onCopyAnswer(turn)}
                            />
                          )}
                        </div>
                      </div>

                      {filterStatsLine && (
                        <p className="vc-filter-stats">{filterStatsLine}</p>
                      )}

                      <AnswersEvidenceTray
                        className="answers-references-card--copy"
                        onSelectSource={(src) => {
                          onSetSelectedSource(src);
                          onSetPdfLoadError('');
                        }}
                        references={references}
                        selectedSource={selectedSource}
                        turn={turn}
                        updateTurn={updateTurn}
                      />

                      {turn.response?.suggested_questions?.length > 0 && (
                        <div className="answers-copy-suggestions">
                          <p>{isNoInformationAnswer(turn) ? 'Try asking:' : 'You might also ask:'}</p>
                          <div>
                            {turn.response.suggested_questions.map((q) => (
                              <button key={q} type="button" onClick={() => onSuggestedQuestion(q)}>
                                {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}

              {isQuerying && (
                <div className="version-card active version-card--loading">
                  <div className="vc-content vc-content--loading">
                    <Loader2 className="animate-spin" size={20} color="var(--text3)" />
                    <span>Processing engine rules...</span>
                  </div>
                </div>
              )}
              {error && <div className="answers-copy-query-error">{error}</div>}
            </div>

            <div className="ap-left-input-area">
              <div ref={targetInputRef} className="ask-box" style={{ visibility: morph ? 'hidden' : 'visible' }}>
                <textarea
                  className="ask-textarea"
                  rows={1}
                  placeholder="Ask Answers..."
                  value={inputText}
                  onChange={(e) => onSetInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onSendFollowUp();
                    }
                  }}
                />
                <div className="ask-toolbar">
                  <div className="ask-tb-left" />
                  <div className="ask-tb-right">
                    <button type="button" className="ask-send-btn" onClick={onSendFollowUp} disabled={!inputText.trim() || isQuerying}>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <section className="ap-right">
            <div className="ap-right-content">
              {selectedSource ? (
                <>
                  <div className="answers-copy-source-summary">
                    <h3>{selectedSource.product_name || selectedSource.file_name || 'Source document'}</h3>
                    <p>
                      {selectedSource.serff_id || 'Source'} · Pages {selectedSource.page_start}-{selectedSource.page_end}
                    </p>
                    {selectedPdfUrl && (
                      <a href={selectedPdfUrl} target="_blank" rel="noopener noreferrer">
                        Open PDF in new tab
                      </a>
                    )}
                  </div>
                  {pdfLoadError && <div className="context-pdf-error">{pdfLoadError}</div>}
                  {selectedPdfUrl ? (
                    <div className="answers-copy-pdf-frame">
                      <PdfCitationViewer
                        pdfUrl={selectedPdfUrl}
                        selectedSource={selectedSource}
                        conversation={conversation}
                        onLoadError={handlePdfLoadError}
                        onLoadSuccess={handlePdfLoadSuccess}
                      />
                    </div>
                  ) : selectedSource.text ? (
                    <div className="answers-copy-source-text">{selectedSource.text}</div>
                  ) : null}
                </>
              ) : (
                <div className="ap-right-empty">
                  {isQuerying ? (
                    <>
                      <div className="premium-loader-orb">
                        <div className="orb-ring" />
                        <div className="orb-core" />
                      </div>
                      <div className="ap-right-empty-title">
                        Synthesizing research logs and mapping citations...
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ap-right-empty-icon">
                        <FileText size={32} />
                      </div>
                      <div className="ap-right-empty-title">
                        No static documents associated with this inquiry turn.
                      </div>
                      <p className="ap-right-empty-sub">
                        Any cited regulatory PDFs will automatically populate right here.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default AnswersWorkspace;
